/**
 * frontend/src/context/ThemeContext.jsx
 * Global SignalIQ theme context.
 *
 * Important:
 * ThemeProvider must wrap <App /> in main.jsx so users can choose the theme
 * from public auth pages before logging in and keep the same preference after login.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);
const THEME_STORAGE_KEY = 'nxa_theme';

function readInitialTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // ignore storage failures
  }

  return 'dark';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  const setTheme = (nextTheme) => {
    const cleanTheme = nextTheme === 'light' ? 'light' : 'dark';
    setThemeState(cleanTheme);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, cleanTheme);
    } catch {
      // ignore storage failures
    }
  };

  const toggle = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    root.setAttribute('data-theme', theme);
    body.setAttribute('data-theme', theme);

    root.classList.remove('theme-dark', 'theme-light');
    body.classList.remove('theme-dark', 'theme-light');

    root.classList.add(theme === 'dark' ? 'theme-dark' : 'theme-light');
    body.classList.add(theme === 'dark' ? 'theme-dark' : 'theme-light');

    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
    root.style.background = theme === 'dark' ? '#080f1c' : '#f3f6fb';
    body.style.background = theme === 'dark' ? '#080f1c' : '#f3f6fb';
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      isLight: theme === 'light',
      setTheme,
      toggle,
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }

  return context;
}
